import type { InstallRecord, Lane, LaneManifest, Phase, StorageDiagnostics } from "@/runtime/events";

export interface LaneInstallState {
  manifest: LaneManifest;
  installed: boolean;
  received: number;
  total: number;
  status: "missing" | "installing" | "installed" | "checking" | "ready" | "error";
  record?: InstallRecord;
  errorMessage?: string;
}

export interface NetworkBannerState {
  mode: "checking" | "online" | "offline";
  value: string;
  caption: string;
}

export function createLaneInstallState(
  manifests: Record<Lane, LaneManifest>,
): Record<Lane, LaneInstallState> {
  return {
    vad: {
      manifest: manifests.vad,
      installed: false,
      received: 0,
      total: manifests.vad.bytes,
      status: "missing",
    },
    stt: {
      manifest: manifests.stt,
      installed: false,
      received: 0,
      total: manifests.stt.bytes,
      status: "missing",
    },
    llm: {
      manifest: manifests.llm,
      installed: false,
      received: 0,
      total: manifests.llm.bytes,
      status: "missing",
    },
    tts: {
      manifest: manifests.tts,
      installed: false,
      received: 0,
      total: manifests.tts.bytes,
      status: "missing",
    },
  };
}

export const PHASE_LABELS: Record<Phase, string> = {
  booting: "Booting",
  provisioning: "Provisioning",
  idle: "Idle",
  listening: "Listening",
  speech_detected: "Speech Detected",
  transcribing: "Transcribing",
  thinking: "Thinking",
  speaking: "Speaking",
  interrupted: "Interrupted",
  error: "Error",
};

export const EMPTY_STORAGE: StorageDiagnostics = {
  quota: 0,
  usage: 0,
  available: 0,
  persisted: false,
  persistenceGranted: false,
  opfsAvailable: false,
  persistApiAvailable: false,
  persistedApiAvailable: false,
  permissionState: "unknown",
};

export const CHECKING_NETWORK: NetworkBannerState = {
  mode: "checking",
  value: "Checking",
  caption: "Probing the server so the app can infer whether the device is still online.",
};

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  let value = bytes;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}
